// Unified traffic store. One zustand store, one map of targets keyed
// by ICAO hex. Source-agnostic by design: any source module (internet
// poller, GDL90 receiver, future Connext bridge) calls `pushTargets()`
// with its current view of the world, and the store reconciles via
// `resolvePriority()`.
//
// The map renderer reads `targets` directly. It does NOT care which
// source produced any given row. When the GDL90 source lights up
// mid-flight, the same icons stay on screen but their per-target
// `source` flips to 'gdl90' and their freshness window shrinks. No
// visual flicker, no race condition.

import { create } from 'zustand';
import {
  SOURCE_CONFIGS,
  TrafficSource,
  TrafficTarget,
} from './types';

interface TrafficState {
  /** Targets keyed by ICAO hex. The resolver guarantees each entry
   *  is the freshest copy across all active sources. */
  targets: Map<string, TrafficTarget>;
  /** Active source status: lastSeenMs + lastError per source. Used by
   *  the status pill to render "INET 5s" or "GDL90 0.4s" tags. */
  sourceStatus: Record<TrafficSource, SourceStatus>;
  /** Whether the master traffic toggle is enabled (Layers → Ops). */
  enabled: boolean;

  // ---- Mutations ------------------------------------------------------
  /** Push the latest target batch from a single source. The store
   *  re-resolves priority on the union of {existing targets, new batch}.
   *  Pass an empty array to mark the source healthy with no targets. */
  pushTargets: (source: TrafficSource, batch: TrafficTarget[]) => void;
  /** Note a source-level failure (offline, 429, etc.). Renders in pill. */
  reportSourceError: (source: TrafficSource, error: string | null) => void;
  /** Drop targets older than the per-source freshness window. Called
   *  on a 1Hz timer from the source poller. */
  gc: () => void;
  /** Clear everything. Used when the user toggles the layer off. */
  clear: () => void;
  /** Master enable / disable. Sources should check this before polling. */
  setEnabled: (next: boolean) => void;
}

export interface SourceStatus {
  /** Epoch millis of the last successful push, even if 0 targets. */
  lastSeenMs: number;
  /** Last error string, or null. */
  lastError: string | null;
  /** True when this source has produced at least one push in this session. */
  active: boolean;
}

function emptyStatus(): SourceStatus {
  return { lastSeenMs: 0, lastError: null, active: false };
}

/**
 * Resolve the priority winner for one ICAO. Inputs are all candidate
 * targets for that ICAO across sources. Returns the best one, or null
 * if every candidate is stale per its source's freshness window.
 *
 * Tie-break: lower priority number wins; equal priority → newer
 * lastSeenMs wins. (In practice the GDL90 / Connext sources have
 * priority 0, internet has priority 1, so the receiver ALWAYS wins
 * when fresh.)
 */
function resolvePriority(
  candidates: TrafficTarget[],
  nowMs: number,
): TrafficTarget | null {
  let best: TrafficTarget | null = null;
  let bestKey = [Infinity, -Infinity] as [number, number]; // [priority, lastSeen]
  for (const c of candidates) {
    const cfg = SOURCE_CONFIGS[c.source];
    if (!cfg) continue;
    if (nowMs - c.lastSeenMs > cfg.freshnessMs) continue;
    const key: [number, number] = [cfg.priority, c.lastSeenMs];
    // Lower priority wins; equal priority → newer wins.
    if (
      key[0] < bestKey[0] ||
      (key[0] === bestKey[0] && key[1] > bestKey[1])
    ) {
      best = c;
      bestKey = key;
    }
  }
  return best;
}

export const useTrafficStore = create<TrafficState>((set, get) => ({
  targets: new Map(),
  sourceStatus: {
    internet: emptyStatus(),
    gdl90: emptyStatus(),
    connext: emptyStatus(),
  },
  enabled: false,

  pushTargets: (source, batch) => {
    const now = Date.now();
    const next = new Map(get().targets);

    // Track ICAOs touched in this batch so we don't accidentally
    // resurrect a target that's been removed upstream.
    const touched = new Set<string>();
    for (const t of batch) {
      touched.add(t.icaoHex);
    }

    // Build the merged candidate set: for any ICAO touched in the
    // batch, swap in the new copy as the (source)'s candidate; keep
    // other sources' copies (whose targets ARE still in next from
    // earlier pushes) intact via the existing entries.
    //
    // The store stores ONE row per ICAO (the resolved winner), so to
    // do a real cross-source compare we'd need a richer candidate
    // bag. Simplification: if the incoming target is from a source
    // with priority <= existing.source priority, accept it. Else
    // keep the existing row (it's from a higher-priority source).
    // The gc() sweep will drop the higher-priority row when it goes
    // stale, at which point the next push from a lower-priority
    // source can take over.
    for (const incoming of batch) {
      const existing = next.get(incoming.icaoHex);
      if (!existing) {
        next.set(incoming.icaoHex, incoming);
        continue;
      }
      const existingCfg = SOURCE_CONFIGS[existing.source];
      const incomingCfg = SOURCE_CONFIGS[incoming.source];
      const existingStale =
        existingCfg && now - existing.lastSeenMs > existingCfg.freshnessMs;
      // Accept incoming when it's same-or-higher priority, OR when
      // the existing copy has gone stale.
      if (existingStale || incomingCfg.priority <= existingCfg.priority) {
        next.set(incoming.icaoHex, incoming);
      }
    }

    // If THIS source had previously published an ICAO not present in
    // the new batch, leave the existing row in place — gc() will
    // sweep it when it ages out of the freshness window. This is the
    // correct behavior for polling (gaps between polls don't mean
    // the target disappeared).

    set({
      targets: next,
      sourceStatus: {
        ...get().sourceStatus,
        [source]: {
          lastSeenMs: now,
          lastError: null,
          active: true,
        },
      },
    });
  },

  reportSourceError: (source, error) => {
    set({
      sourceStatus: {
        ...get().sourceStatus,
        [source]: {
          ...get().sourceStatus[source],
          lastError: error,
        },
      },
    });
  },

  gc: () => {
    const now = Date.now();
    let removed = 0;
    const next = new Map(get().targets);
    for (const [hex, t] of next) {
      const cfg = SOURCE_CONFIGS[t.source];
      if (!cfg) { next.delete(hex); removed++; continue; }
      if (now - t.lastSeenMs > cfg.freshnessMs) {
        next.delete(hex);
        removed++;
      }
    }
    if (removed > 0) set({ targets: next });
  },

  clear: () => {
    set({ targets: new Map() });
  },

  setEnabled: (next) => {
    set({ enabled: next });
    if (!next) set({ targets: new Map() });
  },
}));

// GC sweep — drops stale targets at 1 Hz. Cheap (O(N) over targets;
// <500 even over LA at 200 nm). Module-scope `setInterval` used to run
// unconditionally, including in backgrounded tabs (HI-3 audit
// 2026-05-22). Now exposed as start/stop so `useAppVisibility` can
// pause it when the tab is hidden.
let trafficGcTimer: ReturnType<typeof setInterval> | null = null;
export function startTrafficGc(): void {
  if (typeof window === 'undefined') return;
  if (trafficGcTimer != null) return;
  trafficGcTimer = setInterval(() => useTrafficStore.getState().gc(), 1000);
}
export function stopTrafficGc(): void {
  if (trafficGcTimer != null) {
    clearInterval(trafficGcTimer);
    trafficGcTimer = null;
  }
}
// Boot at module load — backwards-compatible with prior unconditional
// interval. `useAppVisibility` then takes over pause/resume.
startTrafficGc();

/** Snapshot helper: array of targets in stable order (sorted by
 *  icaoHex) for GeoJSON rendering. */
export function selectTargets(state: TrafficState): TrafficTarget[] {
  const arr = Array.from(state.targets.values());
  arr.sort((a, b) => (a.icaoHex < b.icaoHex ? -1 : a.icaoHex > b.icaoHex ? 1 : 0));
  return arr;
}

// Suppress unused warnings for resolvePriority — exported for tests /
// future multi-candidate path.
export { resolvePriority };
